import React, { useState, useCallback, useRef, useEffect } from 'react';
import * as XLSX from 'xlsx';
import { getFunctions, httpsCallable } from 'firebase/functions';
import {
  Upload, FileSpreadsheet, Loader2, CheckCircle2, AlertTriangle, X, ShieldCheck, ListChecks,
} from 'lucide-react';
import { parseMdy } from '../utils/cpscParse';
import { KIND } from './CpscUploadPreflight';

/**
 * CpscUpload — CHA-168. Bureau Veritas (BV) test-report ingestion under Imports.
 *
 * Operator drops a BV "Detail Level" tracking export; we parse it client-side (SheetJS), do a fast
 * shape pre-check, and hand the exact header row + raw cells to the `saveCpscUpload` callable, which
 * is authoritative for validation, Report-No. grouping, citation derivation, mapping, and storage
 * (write-path B — top-level locked `/cpsc_uploads/...`, server-only writes). No CPSC submission and no
 * review/defaults here — CHA-170 owns the four-label review UI; CHA-171/172 own submission.
 *
 * SKU header-collision trap (Stage 1): the export carries BOTH `'SKU # '` (trailing space) and
 * `'SKU #'`. We read headers EXACT and UNTRIMMED so the two never collapse — trimming (as the generic
 * SmartDropZone does) would re-introduce the collision. Rows are sent as position-aligned arrays.
 */
const s = (v) => String(v ?? '').trim();

// Detail-Level signature — mirrors the server's REQUIRED_HEADERS. A missing column means the
// "Request List" starter template or a non-Detail-Level export. Pre-checked here for fast feedback;
// the server re-validates authoritatively.
const REQUIRED_HEADERS = ['Report No.', 'Test Name', 'Service Result', 'Style No.'];

// Read a BV workbook into { sheetName, headers (exact, untrimmed), rows (position-aligned arrays) }.
function parseWorkbook(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(e.target.result, { type: 'binary' });
        const sheetName = wb.SheetNames[0];
        const ws = wb.Sheets[sheetName];
        const grid = XLSX.utils.sheet_to_json(ws, { header: 1, defval: null });
        if (!grid.length) return reject(new Error('The sheet is empty.'));

        // Header row = the row with the most non-empty cells in the first 10 (BV puts it at row 1,
        // but be defensive). Headers are kept EXACTLY as-is (no trim) to preserve the SKU collision.
        let headerIdx = 0, maxCols = 0;
        for (let i = 0; i < Math.min(grid.length, 10); i += 1) {
          const count = (grid[i] || []).filter((c) => c != null && String(c).trim() !== '').length;
          if (count > maxCols) { maxCols = count; headerIdx = i; }
        }
        const headers = (grid[headerIdx] || []).map((h) => (h == null ? '' : String(h)));

        // Data rows: align each to headers.length by position; drop fully-empty rows.
        const rows = [];
        for (let i = headerIdx + 1; i < grid.length; i += 1) {
          const raw = grid[i] || [];
          if (!raw.some((c) => c != null && String(c).trim() !== '')) continue;
          const row = new Array(headers.length).fill(null);
          for (let j = 0; j < headers.length; j += 1) row[j] = raw[j] == null ? null : raw[j];
          rows.push(row);
        }
        resolve({ sheetName, headers, rows });
      } catch (err) {
        reject(new Error(`Could not read ${file.name}: ${err.message}`));
      }
    };
    reader.onerror = () => reject(new Error(`Could not read ${file.name}.`));
    reader.readAsBinaryString(file);
  });
}

// Client-side preview stats from the parsed grid (server stays authoritative on the stored values).
function buildPreview({ headers, rows }) {
  const idx = (name) => headers.indexOf(name);
  const missingHeaders = REQUIRED_HEADERS.filter((h) => idx(h) < 0);
  const reportCol = idx('Report No.');
  const dateCol = idx('Report Date');
  const reportNos = new Set();
  let badDates = 0;
  rows.forEach((r) => {
    if (reportCol >= 0) reportNos.add(s(r[reportCol]) || '(none)');
    if (dateCol >= 0 && s(r[dateCol]) && !parseMdy(s(r[dateCol]))) badDates += 1;
  });
  return { missingHeaders, reportCount: reportNos.size, rowCount: rows.length, badDates };
}

// CHA-208 + CHA-223: parse a BV "Report Level" export into { entries: [{ reportNo, productList, colors }], unresolved }.
// The Report-Level `Style No.` is the AUTHORITATIVE FULL product list per report (the Detail-Level export
// under-lists). Product identity MIRRORS the server's collectProductIds (functions/cpscUpload.js): read
// `Style No.` first, then fall back to the SKU column(s) for Walmart (Style No. blank; products in SKU — CHA-203).
// Lists split on comma/semicolon/slash (the server re-cleans too). `Style Description` (or `Color`) carries the
// aligned per-style colors. Invariant (CHA-223): a report row with a Report No. but NO product in any mapped
// column goes to `unresolved` so the caller surfaces it LOUDLY — a PASS report must resolve its styles or fail
// loud, never silently drop.
const SKU_HEADER_RE = /^\s*(sku\b|master\s*sku|sku\s*#|sku\s*number|sku\s*\/\s*skn|skn\s*\/\s*sku)/i;
export function parseReportLevel({ headers, rows }) {
  const idx = (n) => headers.indexOf(n);
  const rc = idx('Report No.'); const sc = idx('Style No.'); const cc = idx('Color'); const dc = idx('Style Description');
  // SKU-ish columns by EXACT/untrimmed header (preserves the 'SKU # '/'SKU #' collision contract). Same regex as the server.
  const skuCols = (headers || []).map((h, i) => [h, i]).filter(([h]) => SKU_HEADER_RE.test(String(h == null ? '' : h))).map(([, i]) => i);
  const out = []; const unresolved = [];
  if (rc < 0) return { entries: out, unresolved };
  for (const r of rows || []) {
    const rn = r[rc] == null ? '' : String(r[rc]).trim();
    if (!rn) continue;
    // Style No. first; if blank, fall back to the first SKU column with data (CHA-223 — Walmart carries products in SKU).
    let prodRaw = (sc >= 0 && r[sc] != null) ? String(r[sc]) : '';
    if (!prodRaw.trim()) { for (const si of skuCols) { const v = r[si] == null ? '' : String(r[si]); if (v.trim()) { prodRaw = v; break; } } }
    if (!prodRaw.trim()) { unresolved.push(rn); continue; } // no product in Style No. NOR any SKU column → surface, don't drop
    const colorRaw = (cc >= 0 && r[cc] != null && String(r[cc]).trim()) ? String(r[cc]) : (dc >= 0 && r[dc] != null ? String(r[dc]) : '');
    const styles = prodRaw.split(/[,;/]+/).map((s) => s.trim()).filter(Boolean);
    // Products split on comma/semicolon/slash; COLORS split on comma/semicolon ONLY — a slash can be part of a
    // colorway name (e.g. "BLACK/WHITE"), and downstream (cpscDraft/cpscSubmit) never splits colors on "/" (Codex).
    const colors = colorRaw.split(/[,;]+/).map((s) => s.trim());
    const seen = new Map();
    styles.forEach((pid, i) => { if (!seen.has(pid)) seen.set(pid, colors[i] || colors[0] || ''); });
    if (seen.size) out.push({ reportNo: rn, productList: [...seen.keys()], colors: [...seen.values()] });
    else unresolved.push(rn);
  }
  return { entries: out, unresolved };
}

// CHA-267 PR-2 (Ruling 2): BV is the batch lane — it short-circuits to the ingest summary and hands off to
// Drafts (where its per-field provenance review lives), never the single-record Review. `onGoToDrafts` is the
// optional nav hook the Create flow threads in; absent (legacy mount), the text-only pointer remains.

/**
 * The operator-facing "why did my upload drop things" line. Extracted and exported (CHA-498) so it can be
 * unit-tested: every reason here is a promise that a silent skip becomes visible, and until now none of them
 * had a test — CHA-486's `unusable product ID` line shipped uncovered too, and CHA-498's dismissed line was
 * missed entirely on the first pass, which is exactly the failure this guards.
 *
 * Each reason is deliberately NAMED, never merged: "duplicate", "dismissed" and "no product ID" send the
 * operator to three different places, and collapsing them would send them to the wrong one.
 */
export function buildSkipNote(d = {}) {
  const skips = [];
  if (d.unfinishedReports?.length) skips.push(`${d.unfinishedReports.length} not finished`);
  if (d.skippedDuplicates?.length) skips.push(`${d.skippedDuplicates.length} duplicate`);
  // CHA-498 — dismissed reports are blocked server-side, but a silent block reads as "nothing happened".
  // Carries the remedy, because the operator's next question is "why did my upload do nothing?".
  if (d.skippedDismissed?.length) skips.push(`${d.skippedDismissed.length} dismissed — restore the draft first to re-ingest`);
  if (d.missingProductIdReports?.length) skips.push(`${d.missingProductIdReports.length} no product ID`);
  // CHA-486 — brand-as-style rows are skipped at ingest rather than becoming unfixable error drafts. Named
  // separately from "no product ID": one is an empty column, this one is a value that cannot be a product id.
  if (d.invalidProductIdReports?.length) skips.push(`${d.invalidProductIdReports.length} unusable product ID (brand as style)`);
  if (d.missingReportNoRows) skips.push(`${d.missingReportNoRows} no report #`);
  if (d.reconciled) skips.push(`${d.reconciled} draft(s) updated from Report-Level`); // CHA-208
  return skips.length ? skips.join(' · ') : null;
}

// CHA-666 — the non-skip outcome line: spaced product-ID fixes the operator accepted, and spaced IDs the server
// could not safely fix (those still become error drafts, as before, and need correcting in the source file).
export function buildFixNote(d = {}) {
  const parts = [];
  if (d.productIdFixCount) parts.push(`${d.productIdFixCount} product ID fix${d.productIdFixCount === 1 ? '' : 'es'} applied`);
  if (d.productIdFlags?.length) parts.push(`${d.productIdFlags.length} product ID${d.productIdFlags.length === 1 ? '' : 's'} with a stray space — correct in the source file`);
  return parts.length ? parts.join(' · ') : null;
}

// CHA-666 — the ticked proposals, in the shape saveCpscUpload re-checks against its own proposals.
export function chosenProductIdFixes(issues, ticks) {
  return (issues || []).filter((i) => i.kind === 'propose' && ticks[`${i.reportNo}\u0000${i.from}`])
    .map((i) => ({ reportNo: i.reportNo, from: i.from, to: i.willBe }));
}

// CHA-666 — product IDs split by a stray space, shown BEFORE the file is ingested. Proposals start unticked (the
// CHA-544 convention for a judgement call); a flag has no fix. Ingesting with nothing ticked = the file as uploaded.
export function ProductIdReview({ review, onDecide }) {
  const [ticks, setTicks] = useState({});
  const issues = review.issues || [];
  const fixes = chosenProductIdFixes(issues, ticks);
  const byReport = new Map();
  for (const i of issues) { if (!byReport.has(i.reportNo)) byReport.set(i.reportNo, []); byReport.get(i.reportNo).push(i); }
  return (
    <div className="bg-carbon-white border border-carbon-border p-3 space-y-3" data-testid="productid-review">
      <div>
        <h4 className="text-sm font-semibold text-carbon-ink">Product IDs with a stray space: {review.fileName}</h4>
        <p className="text-xs text-carbon-gray-70 mt-0.5">
          CPSC rejects a product ID that contains a space (error 1004), and it can't match a style. Tick the fixes you accept. Nothing is ingested until you choose.
        </p>
      </div>
      <div className="space-y-2 max-h-[28rem] overflow-y-auto">
        {[...byReport.entries()].map(([reportNo, list]) => (
          <div key={reportNo} className="border border-carbon-border">
            <div className="px-2 py-1 bg-carbon-gray-10 text-xs font-plex-mono font-semibold">Report {reportNo}</div>
            <table className="w-full text-xs"><tbody>
              {list.map((i) => {
                const key = `${i.reportNo}\u0000${i.from}`;
                const k = i.kind === 'propose' ? KIND.propose : KIND.flag;
                return (
                  <tr key={key} className="border-t border-carbon-border align-top" data-testid={`productid-issue-${i.from}`}>
                    <td className="px-2 py-1 w-6">
                      {i.kind === 'propose' ? (
                        <input type="checkbox" aria-label={`Apply fix: ${i.from} to ${i.willBe}`} checked={!!ticks[key]}
                          onChange={(e) => setTicks((t) => ({ ...t, [key]: e.target.checked }))} />
                      ) : <AlertTriangle size={12} className="text-carbon-red-60 mt-0.5" aria-label="No automatic fix" />}
                    </td>
                    <td className="px-2 py-1"><span className={`inline-block border px-1 text-[10px] uppercase tracking-[0.04em] ${k.cls}`}>{k.label}</span></td>
                    <td className="px-2 py-1 w-full">
                      <div className="font-plex-mono">
                        <span className="text-carbon-gray-80">"{i.from}"</span>
                        {i.kind === 'propose' ? <> → <span className="text-carbon-ink">{i.willBe}</span></> : null}
                      </div>
                      <div className="text-carbon-gray-70">{i.reason}</div>
                    </td>
                  </tr>
                );
              })}
            </tbody></table>
          </div>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" onClick={() => onDecide({ fixes })} data-testid="productid-ingest"
          className="px-2 py-1 text-xs font-semibold bg-carbon-ink text-carbon-white hover:bg-carbon-gray-80">
          {fixes.length ? `Ingest with ${fixes.length} fix${fixes.length === 1 ? '' : 'es'}` : 'Ingest as uploaded'}
        </button>
        <button type="button" onClick={() => onDecide(null)} data-testid="productid-cancel"
          className="px-2 py-1 text-xs border border-carbon-gray-30 text-carbon-gray-80 hover:bg-carbon-gray-10">Cancel this file</button>
      </div>
    </div>
  );
}

export default function CpscUpload({ userProfile, onGoToDrafts } = {}) {
  // Both global platform roles (platform_owner, platform_admin) clear the gate — the server's
  // resolveTenant accepts both, so the UI must too (Codex P2). Tenant 'admin' clears for its tenant.
  const role = userProfile?.role;
  const isPlatformRole = role === 'platform_owner' || role === 'platform_admin';
  const isAdmin = isPlatformRole || role === 'admin';
  // Platform roles are tenantless → pass tenantId explicitly (CHA-164); tenant admins act on their own.
  const activeTenantId = userProfile?.tenantId || 'inmocean';

  const [parsed, setParsed] = useState(null);     // { fileName, sheetName, headers, rows }
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);     // callable response summary
  const [dragOver, setDragOver] = useState(false);
  const [queue, setQueue] = useState([]);          // multi-file: [{ fileName, status, reportCount, error }]
  const [queueBusy, setQueueBusy] = useState(false);
  const [review, setReview] = useState(null);      // CHA-666 — { fileName, issues, resolve } while a file awaits product-ID review
  const reviewRef = useRef(null);
  useEffect(() => { reviewRef.current = review; }, [review]);
  // Leaving the page mid-review cancels that file instead of leaving the ingest loop waiting forever.
  useEffect(() => () => { if (reviewRef.current) reviewRef.current.resolve(null); }, []);
  const fileInputRef = useRef(null);

  const reset = () => { setParsed(null); setPreview(null); setError(null); setResult(null); };

  // Multi-file: parse + auto-ingest EACH selected/dropped file, one at a time. The Detail-Level shape check
  // still gates each file (a bad file is flagged 'blocked' + skipped, never uploaded). Per-file status list.
  const handleFiles = useCallback(async (fileList) => {
    const all = Array.from(fileList || []).filter((f) => /\.(xlsx|xls)$/i.test(f.name));
    if (!all.length) { setError('Drop .xlsx / .xls BV export(s).'); return; }
    // CHA-208: split off any Report-Level export(s) by BV's filename — they supply the AUTHORITATIVE full product
    // list per report. Parse them into a reportNo→{productList,colors} map, then pass it when ingesting the
    // Detail-Level file(s) (which bring the citations). Detail alone under-lists a report's styles.
    const isReportLevel = (f) => /report[_ ]?level/i.test(f.name);
    // Test-Level / Test-Level-Substance are NOT part of the Report+Detail model — they under-list products AND
    // would double-ingest the same reports. Skip them (BV mass-download emits all 4 levels; only 2 are used).
    const isTestLevel = (f) => /test[_ ]?level/i.test(f.name);
    const rlFiles = all.filter(isReportLevel);
    const skipFiles = all.filter((f) => !isReportLevel(f) && isTestLevel(f));
    const files = all.filter((f) => !isReportLevel(f) && !isTestLevel(f)); // Detail-Level (+ unknown) → ingest
    setError(null); setResult(null); setParsed(null); setPreview(null);
    setQueue([
      ...rlFiles.map((f) => ({ fileName: f.name, status: 'pending', role: 'product list source' })),
      ...files.map((f) => ({ fileName: f.name, status: 'pending' })),
      ...skipFiles.map((f) => ({ fileName: f.name, status: 'skipped', skipNote: 'Test-Level not used — upload Report-Level + Detail-Level' })),
    ]);
    setQueueBusy(true);
    // Parse Report-Level file(s) first → merged map (last write wins per report).
    const rlMap = new Map();
    for (let i = 0; i < rlFiles.length; i++) {
      const f = rlFiles[i];
      const upd = (patch) => setQueue((prev) => prev.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
      upd({ status: 'parsing' });
      try {
        const out = await parseWorkbook(f);
        const { entries, unresolved } = parseReportLevel(out);
        entries.forEach((e) => rlMap.set(e.reportNo, e));
        // CHA-223: a report with no resolvable product (not in Style No. NOR any SKU column) means the product
        // lives in an unmapped column → new template needed. Surface it LOUDLY, never a silent skip.
        // Clean parse → no skipNote (the display appends " skipped", which would misread a fully-resolved
        // product-list source as a skip — CodeRabbit). Only the unresolved case (a report whose product is in an
        // unmapped column) sets the loud note, and those reports genuinely WERE skipped.
        const note = unresolved.length
          ? `⚠ ${unresolved.length} report(s) with no resolvable product`
          : null;
        upd({ status: 'done', reportCount: entries.length, skipNote: note, unresolved: unresolved.length || undefined });
      } catch (e) { console.error('BV report-level parse failed', f.name, e); upd({ status: 'error', error: e?.message || 'parse failed' }); }
    }
    const reportLevel = [...rlMap.values()];
    const fn = httpsCallable(getFunctions(), 'saveCpscUpload', { timeout: 330000 }); // CHA-382 — server's 300s window + 30s transport headroom (Codex, PR #298: a response finishing near the server deadline must not be cut client-side)
    for (let i = 0; i < files.length; i++) {
      const qi = rlFiles.length + i; // queue is [rlFiles..., files...]
      const f = files[i];
      const upd = (patch) => setQueue((prev) => prev.map((it, idx) => (idx === qi ? { ...it, ...patch } : it)));
      upd({ status: 'parsing' });
      try {
        const out = await parseWorkbook(f);
        const pv = buildPreview(out);
        if (pv.missingHeaders?.length) { upd({ status: 'blocked', error: `Not Detail Level — missing ${pv.missingHeaders.join(', ')}` }); continue; }
        upd({ status: 'uploading', reportCount: pv.reportCount });
        const payload = { fileName: f.name, sheetName: out.sheetName, headers: out.headers, rows: out.rows, ...(reportLevel.length ? { reportLevel } : {}), ...(isPlatformRole ? { tenantId: activeTenantId } : {}) };
        let res = await fn(payload);
        // CHA-666 — the server found product IDs split by a stray space and wrote nothing. Pause this file for the
        // operator's decision, then send it again with the ticked fixes (the server re-checks every one).
        if (res?.data?.needsProductIdReview) {
          upd({ status: 'review' });
          const decision = await new Promise((resolve) => setReview({ fileName: f.name, issues: res.data.productIdIssues || [], resolve }));
          setReview(null);
          if (!decision) { upd({ status: 'blocked', error: 'Cancelled at the product ID review — nothing ingested' }); continue; }
          upd({ status: 'uploading' });
          res = await fn({ ...payload, productIdReviewed: true, productIdFixes: decision.fixes });
        }
        const d = res?.data || {};
        // Server is authoritative for how many actually ingested + why any were dropped (not finished /
        // duplicate / blank report no) — never fall back to the client's parse count, which counts them all.
        upd({ status: 'done', reportCount: (d.reportCount ?? d.reports?.length ?? 0), skipNote: buildSkipNote(d), fixNote: buildFixNote(d) });
      } catch (e) { console.error('BV multi-upload failed', f.name, e); upd({ status: 'error', error: e?.message || 'failed' }); }
    }
    setQueueBusy(false);
  }, [isPlatformRole, activeTenantId]);

  const onDrop = useCallback((e) => {
    e.preventDefault(); setDragOver(false);
    handleFiles(e.dataTransfer.files);
  }, [handleFiles]);

  const upload = useCallback(async () => {
    if (!parsed) return;
    setUploading(true); setError(null); setResult(null);
    try {
      const fn = httpsCallable(getFunctions(), 'saveCpscUpload', { timeout: 330000 }); // CHA-382 — server's 300s window + 30s transport headroom (Codex, PR #298: a response finishing near the server deadline must not be cut client-side)
      const payload = {
        fileName: parsed.fileName,
        sheetName: parsed.sheetName,
        headers: parsed.headers,
        rows: parsed.rows,
        ...(isPlatformRole ? { tenantId: activeTenantId } : {}),
      };
      const res = await fn(payload);
      setResult(res?.data || null);
    } catch (e) {
      setError(e?.message || 'Upload failed.');
    } finally {
      setUploading(false);
    }
  }, [parsed, isPlatformRole, activeTenantId]);

  if (!isAdmin) {
    return (
      <div className="font-plex text-sm text-carbon-gray-70 bg-carbon-gray-10 border border-carbon-border px-4 py-6 text-center">
        BV Upload is restricted to administrators.
      </div>
    );
  }

  const blocked = !!preview?.missingHeaders?.length;
  const busy = uploading || queueBusy;

  return (
    <div className="font-plex text-carbon-ink space-y-4 max-w-2xl">
      <div>
        <h3 className="text-base font-semibold text-carbon-ink">CPSC — BV Test Report Upload</h3>
        <p className="text-sm text-carbon-gray-70">
          Drop a Bureau Veritas <strong>Detail Level</strong> tracking export (.xlsx). We parse it,
          group by Report No., and derive the citation from each Test Name. Review &amp; submission
          happen in later steps.
        </p>
      </div>

      {isPlatformRole && (
        <div className="text-xs text-carbon-blue-70 bg-carbon-blue-10 border border-carbon-blue-60 px-3 py-2">
          Acting as <strong>{role}</strong> against tenant: <strong>{activeTenantId}</strong>
        </div>
      )}

      {/* Drop zone — keyboard-accessible (role=button + Enter/Space); inert while busy so an
          in-flight parse/upload can't be interrupted by a second file selection (CodeRabbit). */}
      <div
        role="button"
        tabIndex={busy ? -1 : 0}
        aria-disabled={busy}
        aria-label="Upload a BV Detail Level export"
        onDrop={(e) => { if (!busy) onDrop(e); else e.preventDefault(); }}
        onDragOver={(e) => { e.preventDefault(); if (!busy) setDragOver(true); }}
        onDragLeave={(e) => { e.preventDefault(); setDragOver(false); }}
        onClick={() => { if (!busy) fileInputRef.current?.click(); }}
        onKeyDown={(e) => { if (!busy && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); fileInputRef.current?.click(); } }}
        className={`relative border-2 border-dashed p-8 text-center transition-all focus:outline-none focus:ring-2 focus:ring-carbon-blue-60 ${
          busy ? 'cursor-not-allowed opacity-60 border-carbon-gray-30 bg-carbon-gray-10' : 'cursor-pointer ' + (dragOver ? 'border-carbon-blue-60 bg-carbon-blue-10' : 'border-carbon-gray-30 bg-carbon-gray-10 hover:border-carbon-blue-60')
        }`}
      >
        <input
          ref={fileInputRef} type="file" accept=".xlsx,.xls" multiple className="hidden" disabled={busy}
          onChange={(e) => { handleFiles(e.target.files); if (fileInputRef.current) fileInputRef.current.value = ''; }}
        />
        <div className="flex flex-col items-center gap-2">
          <div className={`p-3 ${dragOver ? 'bg-carbon-blue-10' : 'bg-carbon-white border border-carbon-border'}`}>
            {queueBusy ? <Loader2 size={28} className="text-carbon-blue-60 animate-spin" /> : <FileSpreadsheet size={28} className="text-carbon-gray-40" />}
          </div>
          <p className="text-sm font-medium text-carbon-ink">
            {queueBusy ? 'Ingesting files…' : 'Drop the BV Report-Level + Detail-Level exports, or click to choose'}
          </p>
          <p className="font-plex-mono text-[10.5px] text-carbon-gray-50">.xlsx / .xls — Report-Level gives the full product list, Detail-Level the citations. Test-Level files are ignored.</p>
        </div>
      </div>

      {error && (
        <div className="text-sm text-carbon-red-60 bg-carbon-white border border-carbon-border border-l-[3px] border-l-carbon-red-60 px-3 py-2 flex items-start gap-1">
          <AlertTriangle size={14} className="mt-0.5 shrink-0" /><span>{error}</span>
        </div>
      )}

      {/* Multi-file ingest queue — per-file status + a summary */}
      {queue.length > 0 && (
        <div className="bg-carbon-white border border-carbon-border divide-y divide-carbon-gray-10">
          <div className="px-3 py-2 font-plex-mono text-[10px] uppercase tracking-[0.12em] text-carbon-gray-50 flex items-center justify-between">
            <span>{queue.length} file{queue.length === 1 ? '' : 's'}</span>
            {!queueBusy && <button onClick={() => setQueue([])} className="text-carbon-gray-50 hover:text-carbon-ink uppercase">Clear</button>}
          </div>
          {queue.map((it, i) => (
            <div key={i} className="px-3 py-2 flex items-center gap-2 text-sm">
              <FileSpreadsheet size={14} className="text-carbon-gray-40 shrink-0" />
              <span className="truncate flex-1 text-carbon-gray-80 font-plex-mono text-[12px]">{it.fileName}</span>
              {it.status === 'pending' && <span className="font-plex-mono text-[10.5px] uppercase text-carbon-gray-50">queued</span>}
              {it.status === 'review' && <span className="font-plex-mono text-[10.5px] uppercase text-carbon-orange-70 flex items-center gap-1"><AlertTriangle size={12} />needs review</span>}
              {(it.status === 'parsing' || it.status === 'uploading') && <span className="font-plex-mono text-[10.5px] uppercase text-carbon-blue-60 flex items-center gap-1"><Loader2 size={11} className="animate-spin" />{it.status === 'parsing' ? 'parsing' : 'ingesting'}…</span>}
              {it.status === 'done' && <span className="font-plex-mono text-[10.5px] text-carbon-green-50 flex items-center gap-1"><CheckCircle2 size={12} />{it.reportCount} report{it.reportCount === 1 ? '' : 's'}{it.skipNote ? <span className="text-carbon-orange-70">· {it.skipNote} skipped</span> : null}{it.fixNote ? <span className="text-carbon-gray-70">· {it.fixNote}</span> : null}</span>}
              {it.status === 'blocked' && <span className="font-plex-mono text-[10.5px] uppercase text-carbon-orange-70 flex items-center gap-1" title={it.error}><AlertTriangle size={12} />skipped</span>}
              {it.status === 'error' && <span className="font-plex-mono text-[10.5px] uppercase text-carbon-red-60 flex items-center gap-1" title={it.error}><AlertTriangle size={12} />error</span>}
            </div>
          ))}
          {!queueBusy && (() => {
            const done = queue.filter((q) => q.status === 'done');
            const reports = done.reduce((n, q) => n + (q.reportCount || 0), 0);
            const bad = queue.filter((q) => q.status === 'blocked' || q.status === 'error').length;
            return (
              <div className="px-3 py-2 text-xs text-carbon-gray-70 bg-carbon-gray-10 flex items-center justify-between gap-2">
                <span>Ingested <strong>{done.length}/{queue.length}</strong> file{queue.length === 1 ? '' : 's'} · <strong>{reports}</strong> report{reports === 1 ? '' : 's'}{bad ? ` · ${bad} skipped/failed` : ''}. Review + certify under CPSC Drafts.</span>
                {/* Gate on INGESTED reports, not done-count (Codex P2): Report-Level files parse to 'done'
                    without any saveCpscUpload call — an RL-only drop must not offer a Drafts handoff. */}
                {onGoToDrafts && reports > 0 && (
                  <button onClick={onGoToDrafts} className="shrink-0 px-2 py-1 font-semibold bg-carbon-ink text-carbon-white hover:bg-carbon-gray-80" aria-label="go to drafts">Go to Drafts →</button>
                )}
              </div>
            );
          })()}
        </div>
      )}

      {review && <ProductIdReview key={review.fileName} review={review} onDecide={(decision) => review.resolve(decision)} />}

      {/* Parsed preview (legacy single-file path — unused by the multi-file flow) */}
      {parsed && preview && !result && (
        <div className="bg-carbon-white border border-carbon-border p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 min-w-0">
              <FileSpreadsheet size={16} className="text-carbon-gray-40 shrink-0" />
              <span className="font-plex-mono font-medium text-carbon-ink truncate">{parsed.fileName}</span>
            </div>
            <button onClick={reset} className="p-1 text-carbon-gray-40 hover:text-carbon-ink" title="Clear"><X size={16} /></button>
          </div>

          <div className="flex flex-wrap gap-2 text-xs">
            <span className="px-2 py-0.5 font-plex-mono text-[10.5px] uppercase border border-carbon-gray-30 text-carbon-gray-70">{preview.rowCount} rows</span>
            <span className="px-2 py-0.5 font-plex-mono text-[10.5px] uppercase border border-carbon-gray-30 text-carbon-gray-70">{preview.reportCount} report{preview.reportCount === 1 ? '' : 's'}</span>
            <span className="px-2 py-0.5 font-plex-mono text-[10.5px] uppercase border border-carbon-gray-30 text-carbon-gray-70">{parsed.headers.length} columns</span>
            {preview.badDates > 0 && (
              <span className="px-2 py-0.5 font-plex-mono text-[10.5px] uppercase border border-carbon-orange-40 text-carbon-orange-70">{preview.badDates} unparseable Report Date{preview.badDates === 1 ? '' : 's'}</span>
            )}
          </div>

          {blocked ? (
            <div className="text-sm text-carbon-red-60 bg-carbon-white border border-carbon-border border-l-[3px] border-l-carbon-red-60 px-3 py-2 flex items-start gap-1">
              <AlertTriangle size={14} className="mt-0.5 shrink-0" />
              <span>
                Not a Detail Level export — missing column{preview.missingHeaders.length === 1 ? '' : 's'}:{' '}
                <strong>{preview.missingHeaders.join(', ')}</strong>. Re-export at Detail Level granularity.
              </span>
            </div>
          ) : (
            <div className="text-xs text-carbon-gray-80 bg-carbon-green-10 border border-carbon-green-50 px-3 py-2 flex items-center gap-1">
              <ShieldCheck size={13} className="text-carbon-green-50" /> Detail Level signature present — ready to ingest.
            </div>
          )}

          <button
            onClick={upload}
            disabled={busy || blocked}
            className="flex items-center gap-2 px-4 py-2 bg-carbon-blue-60 text-white text-sm font-semibold hover:bg-carbon-blue-70 disabled:opacity-50"
          >
            {uploading ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} Ingest upload
          </button>
        </div>
      )}

      {/* Result summary */}
      {result?.ok && (
        <div className="text-sm bg-carbon-green-10 border border-carbon-green-50 px-4 py-3 space-y-2">
          <div className="flex items-center gap-1 text-carbon-gray-80 font-medium"><CheckCircle2 size={15} className="text-carbon-green-50" /> Ingested</div>
          <div className="flex flex-wrap gap-2 text-xs">
            <span className="px-2 py-0.5 font-plex-mono text-[10.5px] uppercase bg-carbon-white border border-carbon-green-50 text-carbon-gray-70">{result.reportCount} report{result.reportCount === 1 ? '' : 's'}</span>
            <span className="px-2 py-0.5 font-plex-mono text-[10.5px] uppercase bg-carbon-white border border-carbon-green-50 text-carbon-gray-70">{result.rowCount} rows</span>
            <span className="px-2 py-0.5 font-plex-mono text-[10.5px] uppercase bg-carbon-white border border-carbon-green-50 text-carbon-gray-70">{result.autoDerived} citations auto-derived</span>
            {result.needsConfirmation > 0 && (
              <span className="px-2 py-0.5 font-plex-mono text-[10.5px] uppercase border border-carbon-orange-40 text-carbon-orange-70">{result.needsConfirmation} need confirmation</span>
            )}
          </div>
          <div className="pt-1 space-y-1">
            {(result.reports || []).map((r) => (
              <div key={r.reportNo} className="flex items-center gap-2 text-xs text-carbon-gray-70">
                <ListChecks size={12} className="text-carbon-gray-40" />
                <span className="font-plex-mono font-medium text-carbon-ink">Report {r.reportNo}</span>
                <span>· {r.rowCount} tests</span>
                {r.styleNo && <span>· style {r.styleNo}</span>}
                {r.summary?.needsConfirmation > 0 && <span className="text-carbon-orange-70">· {r.summary.needsConfirmation} to confirm</span>}
                {r.summary?.allSatisfactory === false && <span className="text-carbon-red-60">· has non-satisfactory result</span>}
              </div>
            ))}
          </div>
          <p className="text-[11px] text-carbon-gray-50">Next: review &amp; field confirmation (CHA-170), then CPSC submission.</p>
          <button onClick={reset} className="text-xs text-carbon-blue-60 hover:text-carbon-blue-70 underline">Upload another file</button>
        </div>
      )}
    </div>
  );
}
